import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, watch } from "vue"
import Icon from "@/components/widgets/icons/Icon.vue"
import UserAvatar from "@/components/profile/UserAvatar.vue"
import {
  UserAdvancedFragmentFragment,
  UsersQuery,
  UsersQueryQuery,
  UsersQueryQueryVariables,
  AssignMissionTeamManagerMutation,
  AssignMissionTeamManagerMutationMutation,
  AssignMissionTeamManagerMutationMutationVariables,
  TerminateMissionStaffingPlacementMutation,
  TerminateMissionStaffingPlacementMutationMutation,
  TerminateMissionStaffingPlacementMutationMutationVariables,
} from "@/gql"
import { useMutation } from "@/hooks/useMutation"
import { useNotification } from "@/hooks/useNotification"
import { useQuery } from "@vue/apollo-composable"
import Modal from "@/components/widgets/common/Modal.vue"
import PrimaryButton from "@/components/widgets/button/PrimaryButton.vue"
import SecondaryButton from "@/components/widgets/button/SecondaryButton.vue"


export default /*#__PURE__*/_defineComponent({
  __name: 'TeamMemberCard',
  props: {
    subtitle: null,
    user: null,
    state: null,
    isMissionManager: { type: Boolean },
    missionLifecycleId: null,
    placementId: null
  },
  setup(__props: any) {

const props = __props as {
  subtitle: string
  user: UserAdvancedFragmentFragment
  state: string
  isMissionManager?: boolean
  missionLifecycleId?: string
  placementId?: string
}



const fullName = `${props.user.firstName} ${props.user.lastName}`

const getRecordIcon = (state: string) => {
  const map: Record<string, string> = {
    active: "check-circle",
    pending: "clock-circle",
    terminated: "close-circle",
    completed: "check-circle",
    archived: "archive",
  }

  return map[state] || "record"
}

const showChangeManagerModal = ref(false)
const showTeamMemberDetailsModal = ref(false)
const selectedUserId = ref<number | null>(null)
const nameContains = ref<string | null>(null)
const searchInput = ref<string>("")
const isChangingManager = ref(false)
const isTerminating = ref(false)
const terminationReason = ref("")
const { mutate } = useMutation()
const { addError, addSuccess } = useNotification()

const usersQueryVariables = computed(() => ({
  per: 10,
  filter: {
    search: nameContains.value || undefined,
    roleName: "Impact Manager",
  },
}))

const { result: usersResult, loading: usersLoading } = useQuery<
  UsersQueryQuery,
  UsersQueryQueryVariables
>(UsersQuery, usersQueryVariables)

let searchTimeout: ReturnType<typeof setTimeout> | null = null

watch(searchInput, (newValue) => {
  // Don't trigger search if a user is already selected
  if (selectedUserId.value) {
    return
  }
  if (searchTimeout) {
    clearTimeout(searchTimeout)
  }
  searchTimeout = setTimeout(() => {
    nameContains.value = newValue || null
  }, 300)
})

watch(selectedUserId, (newValue) => {
  // Clear search input when a user is selected to prevent triggering another query
  if (newValue) {
    searchInput.value = ""
    nameContains.value = null
  }
})

const handleChangeManager = async () => {
  if (!selectedUserId.value) {
    addError("Please select a user")
    return
  }

  if (!props.missionLifecycleId) {
    addError("Mission lifecycle ID is required")
    return
  }

  isChangingManager.value = true

  await mutate<
    AssignMissionTeamManagerMutationMutation,
    AssignMissionTeamManagerMutationMutationVariables
  >({
    mutation: AssignMissionTeamManagerMutation,
    variables: {
      missionLifecycleId: props.missionLifecycleId,
      userId: selectedUserId.value.toString(),
    },
    done: () => {
      isChangingManager.value = false
    },
    onSuccess: () => {
      addSuccess("Mission manager changed successfully")
      showChangeManagerModal.value = false
      selectedUserId.value = null
      nameContains.value = null
      window.location.reload()
    },
    onMutationError: (error) => {
      addError(error.message || "Failed to change mission manager")
    },
  })
}

const closeModal = () => {
  showChangeManagerModal.value = false
  selectedUserId.value = null
  nameContains.value = null
  searchInput.value = ""
}

const usersList = computed(() => {
  if (!usersResult.value?.users?.items) {
    return []
  }
  return usersResult.value.users.items.map((user) => ({
    text: `${user.firstName} ${user.lastName}`,
    value: user.id,
  }))
})

const openTeamMemberDetailsModal = () => {
  showTeamMemberDetailsModal.value = true
}

const closeTeamMemberDetailsModal = () => {
  showTeamMemberDetailsModal.value = false
  terminationReason.value = ""
}

const handleTerminateContract = async () => {
  if (!terminationReason.value.trim()) {
    addError("Please provide a termination reason")
    return
  }

  if (!props.placementId) {
    addError("Placement ID is required")
    return
  }

  isTerminating.value = true

  await mutate<
    TerminateMissionStaffingPlacementMutationMutation,
    TerminateMissionStaffingPlacementMutationMutationVariables
  >({
    mutation: TerminateMissionStaffingPlacementMutation,
    variables: {
      missionStaffingPlacementId: props.placementId,
      terminationReason: terminationReason.value,
    },
    done: () => {
      isTerminating.value = false
    },
    onSuccess: () => {
      addSuccess("Contract terminated successfully")
      closeTeamMemberDetailsModal()
      window.location.reload()
    },
    onMutationError: (error) => {
      addError(error.message || "Failed to terminate contract")
    },
  })
}

return { __sfc: true,props, fullName, getRecordIcon, showChangeManagerModal, showTeamMemberDetailsModal, selectedUserId, nameContains, searchInput, isChangingManager, isTerminating, terminationReason, mutate, addError, addSuccess, usersQueryVariables, usersResult, usersLoading, searchTimeout, handleChangeManager, closeModal, usersList, openTeamMemberDetailsModal, closeTeamMemberDetailsModal, handleTerminateContract, Icon, UserAvatar, Modal, PrimaryButton, SecondaryButton }
}

})